import { Link } from 'react-router-dom';

export function SmsConsentPage() {
  return (
    <>
      <style>{`
        .legal-page h2 { margin-top: 2rem; margin-bottom: 0.5rem; font-size: 1.25rem; }
        .legal-page h3 { margin-top: 1.5rem; margin-bottom: 0.5rem; font-size: 1.1rem; }
        .legal-page p, .legal-page ul, .legal-page ol { margin-bottom: 1rem; }
        .legal-page ul, .legal-page ol { padding-left: 1.5rem; }
        .legal-page li { margin-bottom: 0.5rem; }
        .legal-page .legal-date { color: var(--text-muted); font-size: 0.9rem; margin-bottom: 2rem; }
        .legal-page .example-box { background: #F3F4F6; border: 1px solid var(--border-color); border-radius: var(--radius); padding: 1rem 1.25rem; margin: 1rem 0; font-size: 0.95rem; }
        .legal-page .example-box .label { font-weight: 600; font-size: 0.8rem; text-transform: uppercase; color: var(--text-muted); margin-bottom: 0.5rem; }
        .legal-page .consent-mockup { background: #fff; border: 1px solid var(--border-color); border-radius: var(--radius); padding: 1.25rem; margin: 1rem 0; }
        .legal-page .consent-mockup .checkbox-row { display: flex; align-items: flex-start; gap: 0.5rem; margin-top: 0.75rem; }
        .legal-page .consent-mockup .checkbox-row input { margin-top: 3px; }
        .legal-page .consent-mockup input[type="tel"] { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid var(--border-color); border-radius: var(--radius); font-size: 1rem; margin-top: 0.25rem; box-sizing: border-box; }
        .legal-page .consent-mockup label { font-weight: 600; font-size: 0.9rem; }
      `}</style>
      <div className="container legal-page">
        <div style={{ marginBottom: '2rem' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none', color: 'var(--text-color)' }}>
            <img src="/img/logo.svg" alt="SimplyTelehealth" style={{ width: '36px', height: '36px' }} />
            <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>SimplyTelehealth</span>
          </Link>
        </div>

        <h1>SMS Consent &amp; Opt-In Policy</h1>
        <p className="legal-date">Effective Date: March 26, 2026</p>

        <p>SimplyTelehealth uses SMS text messaging to send visit reminders and facilitate text-based telehealth visits between healthcare providers and patients. This page describes how we collect consent, what messages we send, and how to opt out.</p>

        <h2>1. Consent Disclosure</h2>
        <p>The following disclosure is presented at every point where SMS consent is collected. Depending on the context, the exact wording may vary slightly, but the substance is the same:</p>

        <div className="example-box">
          <div className="label">Patient Consent Disclosure (shown at booking)</div>
          By checking this box, you consent to receive SMS text messages from SimplyTelehealth on behalf of your healthcare provider, including appointment reminders and visit notifications. Message frequency varies. Message and data rates may apply. You can opt out at any time by replying STOP to any message. View our SMS Consent Policy at simplytelehealth.md/sms-consent and Privacy Policy at simplytelehealth.md/privacy.
        </div>

        <div className="example-box">
          <div className="label">Verbal Consent Script (read aloud by provider when obtaining patient consent by phone or in person)</div>
          "Do you agree to receive text message reminders about your upcoming telehealth appointments from SimplyTelehealth on behalf of your healthcare provider? Message and data rates may apply. You can opt out at any time by replying STOP."
        </div>

        <div className="example-box">
          <div className="label">Provider Self-Consent Disclosure (shown during onboarding and settings)</div>
          By checking this box, you consent to receive SMS text messages from SimplyTelehealth, including visit reminders and notifications. Message frequency varies. Message and data rates may apply. You can opt out at any time by replying STOP to any message. View our SMS Consent Policy at simplytelehealth.md/sms-consent and Privacy Policy at simplytelehealth.md/privacy.
        </div>

        <h2>2. How We Collect Consent</h2>
        <p>We collect SMS consent through four methods. No SMS messages are sent without prior consent.</p>

        <h3>2a. Patient Self-Booking (Patient-Initiated)</h3>
        <p>When patients book their own appointments through a practice's online scheduling page, they may provide their phone number. An opt-in checkbox must be actively checked before any SMS messages are sent.</p>

        <div className="consent-mockup">
          <label>Patient Phone <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', fontSize: '0.85rem' }}>(optional)</span></label>
          <input type="tel" placeholder="(555) 123-4567" defaultValue="(555) 123-4567" readOnly />
          <div className="checkbox-row">
            <input type="checkbox" checked disabled />
            <span style={{ fontSize: '0.9rem' }}>I agree to receive SMS visit reminders. Msg &amp; data rates may apply. Reply STOP to opt out.</span>
          </div>
        </div>

        <h3>2b. Provider-Created Visits (Provider-Attested)</h3>
        <p>When a healthcare provider creates a visit on behalf of a patient, they enter the patient's phone number and attest that the patient has verbally consented to receive SMS messages.</p>

        <div className="consent-mockup">
          <label>Patient Phone <span style={{ color: 'var(--text-muted)', fontWeight: 'normal', fontSize: '0.85rem' }}>(optional)</span></label>
          <input type="tel" placeholder="(555) 123-4567" defaultValue="(555) 123-4567" readOnly />
          <div className="checkbox-row">
            <input type="checkbox" checked disabled />
            <span style={{ fontSize: '0.9rem' }}>Patient has consented to receive SMS visit reminders</span>
          </div>
          <p style={{ fontSize: '0.8rem', color: 'var(--accent-color)', margin: '4px 0 0 22px' }}>SMS reminders will be sent to this number.</p>
        </div>

        <h3>2c. SMS Visit Initiation (Implicit Consent via Reply)</h3>
        <p>When a provider starts an SMS text visit, the patient receives an initial message explaining the session and how to opt out. The patient's first reply constitutes consent to the two-way conversation.</p>

        <div className="example-box">
          <div className="label">Initial message sent to patient</div>
          Hi Jane, Dr. Smith from Smith Family Medicine has started a text visit with you. Reply to this number to communicate with your provider. This session expires in 24 hours. Msg &amp; data rates may apply. Reply STOP to opt out.
        </div>

        <h3>2d. Provider Self-Consent (Onboarding)</h3>
        <p>Healthcare providers consent to receive SMS notifications on their own phone number during account setup.</p>

        <div className="consent-mockup">
          <label>Cell Phone <span style={{ color: 'var(--error-color)' }}>*</span></label>
          <input type="tel" placeholder="(555) 123-4567" defaultValue="(555) 987-6543" readOnly />
          <div className="checkbox-row">
            <input type="checkbox" checked disabled />
            <span style={{ fontSize: '0.9rem' }}>I agree to receive SMS visit reminders and notifications at this number. Message and data rates may apply. You can opt out at any time.</span>
          </div>
        </div>

        <h2>3. Types of Messages</h2>

        <h3>Visit Reminders</h3>
        <p>Automated appointment reminders sent before scheduled telehealth visits.</p>
        <div className="example-box">
          <div className="label">Sample message</div>
          Smith Family Medicine: Reminder &mdash; your visit with Dr. Smith is tomorrow (Mon, Mar 30 at 2:00 PM ET). Join: https://app.simplytelehealth.md/visit.html?token=jtkn_abc123<br /><br />Reply STOP to opt out.
        </div>

        <h3>SMS Visit Messages</h3>
        <p>Two-way text messages relayed between a patient and their healthcare provider during a text-based visit. Messages are prefixed with the sender's name for clarity.</p>
        <div className="example-box">
          <div className="label">Sample: Provider to patient</div>
          [Dr. Smith] Keep taking the medication for 3 more days. If symptoms persist, we'll schedule a follow-up.
        </div>
        <div className="example-box">
          <div className="label">Sample: Patient to provider</div>
          [Jane Doe] The swelling went down but I still have some discomfort.
        </div>

        <h3>Session Notifications</h3>
        <p>Automated messages when an SMS visit session ends or expires.</p>
        <div className="example-box">
          <div className="label">Sample: Session ended</div>
          Your text visit with Dr. Smith has ended. If you need further assistance, please contact Smith Family Medicine.
        </div>
        <div className="example-box">
          <div className="label">Sample: Session expired</div>
          Your text visit with Dr. Smith has expired. If you need further assistance, please contact your provider.
        </div>

        <h2>4. Message Frequency</h2>
        <ul>
          <li><strong>Visit reminders:</strong> Up to 2 messages per scheduled appointment (24-hour and 1-hour reminders)</li>
          <li><strong>SMS visit messages:</strong> Varies based on the clinical conversation. Sessions automatically expire after 24 hours.</li>
          <li><strong>Session notifications:</strong> 1 message when a session starts, 1 when it ends or expires</li>
        </ul>

        <h2>5. Opt-Out</h2>
        <p>You can stop receiving SMS messages at any time:</p>
        <ul>
          <li>Reply <strong>STOP</strong> to any message from SimplyTelehealth</li>
          <li>You will receive a confirmation message and no further texts will be sent</li>
          <li>To resume messages, reply <strong>START</strong> or re-consent through the platform</li>
        </ul>

        <h2>6. Help</h2>
        <p>Reply <strong>HELP</strong> to any message for assistance, or contact us at <a href="mailto:support@simplytelehealth.md">support@simplytelehealth.md</a>.</p>

        <h2>7. Costs</h2>
        <p>Message and data rates may apply depending on your mobile carrier and plan. SimplyTelehealth does not charge patients for receiving SMS messages.</p>

        <h2>8. Privacy</h2>
        <p>During SMS visits, messages are relayed through a SimplyTelehealth proxy number. Personal phone numbers of providers and patients are never disclosed to each other. We do not store the content of SMS messages. For full details, see our <Link to="/privacy">Privacy Policy</Link>.</p>

        <h2>9. Contact</h2>
        <p>
          SimplyTelehealth<br />
          Email: <a href="mailto:support@simplytelehealth.md">support@simplytelehealth.md</a><br />
          Website: <a href="https://simplytelehealth.md">simplytelehealth.md</a>
        </p>

        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          <Link to="/privacy" style={{ color: 'var(--text-muted)' }}>Privacy Policy</Link> &middot;{' '}
          <Link to="/terms" style={{ color: 'var(--text-muted)' }}>Terms and Conditions</Link>
        </div>
      </div>
    </>
  );
}
